/**
 * Calls the klaviyo API and sets up all the neccesary bindings to interact with said API
 */
import { KlaviyoAPI, getLanguage, registerEmailInKlaviyo, setLanguage } from '../services/klaviyo';
import { $Q, $Qll } from '../utils/querySelector';
import { createMessage } from './message';

const updateMarketingStatus = (customerEmail) => {
  const form = $Q('.newsletter-form-alternative');
  $Q('[name="contact[email]"]', form).value = customerEmail;
  form.submit();
};

/**
 * updates the data subscribed lists of the container element, used to compare when the user changes
 * something on his/her subscribed lists
 *
 * @param {Array<object>} lists
 * @param {HTMLElement} listContainer
 */
const updateDataAttribute = (lists, listContainer) => {
  const subscribedList = lists
    .filter((l) => l.subscribed.length > 0)
    .map((l) => l.list_id);

  listContainer.setAttribute('data-subscribed-lists', subscribedList.join(','));
};

/**
 * Binds the click event to the update button and performs the corresponding updates on click
 *
 * @param {HTMLElement} listContainer
 * @param {string} email
 */
const bindUpdateEvent = async (listContainer, email) => {
  $Q('.subscriptions__update-button').addEventListener('click', async (e) => {
    const alreadyAcceptsMarketing =
      e.target.dataset.alreadySubscribed === 'true';
    const selectedInputs = $Qll('.subscriptions__list input');
    const messageContainer = $Q('#message-container');

    const loader = $Q('.subscriptions__loader', e.target);

    loader.classList.add('shown');
    registerEmailInKlaviyo(email);

    for (const input of selectedInputs) {
      if (input.checked && input.dataset.subscribed !== 'true') {
        input.setAttribute('data-subscribed', 'true');
        await KlaviyoAPI.subscribeToList(email, input.value);

        // if the user has not been subscribed to shopify, then send the form
        if (!alreadyAcceptsMarketing) {
          updateMarketingStatus(email);
        } else {
          createMessage({
            message: messageContainer.dataset.subscribedMessage,
            messageType: 'success',
            mountOn: messageContainer,
            timeout: 6000,
          });
        }
      } else if (!input.checked && input.dataset.subscribed === 'true') {
        input.setAttribute('data-subscribed', 'false');
        await KlaviyoAPI.unsubscribeFromList(email, input.value);
        createMessage({
          message: messageContainer.dataset.unsubscribedMessage,
          messageType: 'success',
          mountOn: messageContainer,
          timeout: 3000,
        });
      }
    }

    const subscribedLists = selectedInputs.map((input) => ({
      list_id: input.value,
      subscribed: [true],
    }));

    loader.classList.remove('shown');

    updateDataAttribute(subscribedLists, listContainer);
  });
};

/**
 * Renders the klaviyo lists with checkboxes
 *
 * @param {Array<object>} lists array of klaviyo's API lists
 * @param {string} email customer email
 */
const renderLists = (lists, email) => {
  // get the list container
  const listContainer = $Q('.subscriptions__list');

  listContainer.innerHTML = '';

  // ! create a fragment to avoid several updates to the dom
  const fragment = document.createDocumentFragment();

  // * create disposable elements
  const disposableInput = document.createElement('input');
  disposableInput.type = 'checkbox';

  const disposableItem = document.createElement('li');

  const disposableLabel = document.createElement('label');

  const disposableSpan = document.createElement('span');

  // update data attributes to compare to later
  updateDataAttribute(lists, listContainer);

  lists.forEach((list) => {
    // ! clone elemnts without attaching them to the DOM yet
    if (list.list_name != 'Newsletter') {
      return;
    }
    const realInput = disposableInput.cloneNode(true);
    realInput.setAttribute('value', list.list_id);

    const realItem = disposableItem.cloneNode(true);
    const realLabel = disposableLabel.cloneNode(true);
    const realSpan = disposableSpan.cloneNode(false);

    // * if the user is subscribed, some attributes need to be set in order
    // * to identify the use case
    if (list.subscribed.length > 0) {
      realLabel.setAttribute('data-list-id', list.list_id);
      realInput.setAttribute('checked', true);
      realInput.setAttribute('data-subscribed', 'true');
    }

    // * append elements to cloned elements. No updating the DOM yet
    realLabel.appendChild(realInput);
    realLabel.appendChild(realSpan);
    realLabel.appendChild(document.createTextNode(list.list_name));

    realItem.appendChild(realLabel);

    fragment.appendChild(realItem);
  });

  // ! update the DOM at the end of the loop, thus making only one update
  listContainer.appendChild(fragment);

  // bind events
  bindUpdateEvent(listContainer, email);
};

/**
 * Fetchs and renders the different lists
 *
 * @param {string} lang - language to be compared against all the lists' names
 */
const fetchLists = async () => {
  const titleLoader = $Q('.subscriptions__title .subscriptions__loader');
  titleLoader.classList.add('shown');

  // email is previously set on a data- attribute in the my-newsletter-subscriptions.liquid section to access it here
  const email = $Q('.account.my-newsletter-subscriptions').dataset.userEmail;

  // get the lists to render
  const lists = await KlaviyoAPI.getLists(email);

  // hide loader
  titleLoader.classList.remove('shown');

  // render lists with email to pass on to the other events
  renderLists(lists, email);
};

const fetchLocale = async () => {
  const titleLoader = $Q('.languages__section .subscriptions__title .subscriptions__loader');
  const button = $Q('.languages__update-button');
  const select = $Q('#customer-locale');
  const messageContainer = $Q('.languages__section #message-container');
  titleLoader.classList.add('shown');

  // email is previously set on a data- attribute in the my-newsletter-subscriptions.liquid section to access it here
  const email = $Q('.account.my-newsletter-subscriptions').dataset.userEmail;

  // get the lists to render
  const { locale } = await getLanguage(email)

  // hide loader
  titleLoader.classList.remove('shown');

  select.value = locale.split('-')[0]
  select.style.display = 'block';

  button.addEventListener('click', async (e) => {
    const loader = $Q('.subscriptions__loader', e.target);
    loader.classList.add('shown');
    const res = await setLanguage(email, select.value);
    if (res.status === 200) {
      createMessage({
        message: messageContainer.dataset.message,
        messageType: 'success',
        mountOn: messageContainer,
        timeout: 6000,
      });
    } else {
      $Q('.languages__section .subscriptions__error').classList.add('shown');
    }

    loader.classList.remove('shown');
  })
};

(async () => {
  const initNewsletter = async () => {
    const titleLoader = $Q('.subscriptions__title .subscriptions__loader');
    try {
      await fetchLists();
    } catch (e) {
      titleLoader.classList.remove('shown');
      $Q('.subscriptions__error').classList.add('shown');
    }
  }
  const initSelect = async () => {
    const titleLoader = $Q('.languages__section .subscriptions__title .subscriptions__loader');
    try {
      await fetchLocale();
    } catch (e) {
      titleLoader.classList.remove('shown');
      $Q('.languages__section .subscriptions__error').classList.add('shown');
    }
  }
  // ! execute only on newsletter page
  if (window.location.href.includes('my-newsletter-subscription')) {
    document.addEventListener('DOMContentLoaded', () => {
      initNewsletter()
      initSelect()
    });

    // show message when user is subscribed and is new to the subscription
    if (window.location.href.includes('customer_posted')) {
      const messageContainer = $Q('#message-container');
      createMessage({
        message: messageContainer.dataset.subscribedMessage,
        messageType: 'success',
        mountOn: messageContainer,
        timeout: 6000,
      });
    }
  }
})();
